import { v4 as uuid } from 'uuid';
import { CloseIcon } from 'components/icons/CloseIcon';
import { WarningIcon } from 'components/icons/WarningIcon';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import api from 'services/api';
import { RootState } from 'store';
import NoImg from 'assets/img/no-img.jpg';
import useVideo from 'hooks/useVideo';

interface IDeleteVideo {
  videoId: string;
  videoName: string;
  videoHashId: string;
  onCancel: () => void;
}

export default function DeleteVideo({ videoId, videoHashId, videoName, onCancel }: IDeleteVideo) {
  const [permissionError, setPermissionError] = useState(false);
  const [sharedChannels, handleSharedChannels] = useState([] as any[]);
  const location = useLocation();
  const channelList = useSelector((state: RootState) => state.channels.channelList);

  const channelIdRegex = /\/channel\/([^/]+)/;
  const channelMatch = location.pathname.match(channelIdRegex);
  const channelId = channelMatch ? channelMatch[1] : 'home';
  const workspaceIdRegex = /\/workspace\/([^/]+)\//;

  const workspaceMatch = location.pathname.match(workspaceIdRegex);
  const workspaceId = workspaceMatch ? workspaceMatch[1] : sessionStorage.getItem('workspace');

  const userData = useSelector((state: RootState) => state.user.user);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null as any);
  const navigateTo = useNavigate();
  const { video, loading, selectVideo } = useVideo(videoHashId, channelId, videoId);

  useEffect(() => {
    if (videoId) {
      api.get(`api/videos/${videoId}/shares/`).then((res) => {
        handleSharedChannels(res.data);
      });
    }
  }, []);

  useEffect(() => {
    if (selectVideo) {
      setSelectedVideo(selectVideo);
    } else {
      setSelectedVideo(video);
    }
  }, [loading]);

  const DeleteVideo = () => {
    api
      .delete(`api/videos/${videoHashId}/info/`)
      .then(() => {
        onCancel();
        if (location.pathname?.match('/videos/')) {
          navigateTo(channelId === 'home' ? '/videos/all' : `workspace/${workspaceId}/user/channel/${channelId}`, {
            state: {
              fromNotification: true
            }
          });
        }
      })
      .catch(() => {
        setPermissionError(true);
      });
  };

  return (
    <div className={`transparent font-lato w-screen md:w-[520px] md:min-h-[50px]`}>
      <div
        className={`relative border-x border-t bg-gray-bg-dark border-gray-300 ${
          permissionError ? 'bg-[#ffe3e9]' : 'bg-white'
        } pb-2 pt-9 px-5 rounded-t-lg`}
      >
        <div className="flex mb-5">
          <span className="font-bold text-2xl leading-6 text-[#1A1C1D]">Delete video</span>
          <CloseIcon className="ml-auto w-5 h-5 cursor-pointer opacity-70 hover:opacity-100" onClick={onCancel} />
        </div>
      </div>
      {!permissionError ? (
        <>
          <div
            className={`font-lato text-sm leading-[21px] font-bold ${
              permissionError ? 'bg-[#ffe3e9]' : 'bg-white'
            } px-5`}
          >
            <p>Title: {videoName || selectedVideo?.name}</p>
            <div className="border rounded-md p-2 mt-4 h-20 overflow-y-auto">
              {sharedChannels.filter((channel: any) => !channel.is_archived).length ? (
                <p className="text-[#8083A3] line-clamp-3">
                  Shared in
                  {sharedChannels
                    .map(
                      (channel: any) => channelList.find((item: any) => item.hash_id === channel.channel_hash_id)?.name
                    )
                    .filter((item) => item)
                    .map((item: string, idx: number) => (
                      <span className="text-[#1664A3]" key={uuid()}>
                        {idx !== 0 && ', '}
                        {' #'}
                        {item}
                      </span>
                    ))}
                </p>
              ) : (
                <p className="text-[#8083A3]">Not shared in any channels</p>
              )}
            </div>
          </div>
          <div className={`flex items-center pt-5 px-5 ${permissionError ? 'bg-[#ffe3e9]' : 'bg-white'}`}>
            <img
              className="rounded-full cursor-pointer w-8 h-8 mr-2"
              src={selectedVideo?.owner?.avatar ? selectedVideo.owner.avatar : NoImg}
              alt="avatar"
            />
            <div className="text-sm text-[#1A1C1D]">
              <p className="font-bold">
                Owned by {selectedVideo?.owner.user_id === userData.user_id ? 'you' : selectedVideo?.owner?.name}
              </p>
              {/* <p className="font-normal text-[#8083A3]">Project Manager</p> */}
            </div>
          </div>
          <div className={`flex items-center ${permissionError ? 'bg-[#ffe3e9]' : 'bg-white'} px-5 py-5`} key={uuid()}>
            <input
              type="checkbox"
              id={`confirm-button`}
              className="focus:ring-0 rounded-sm w-[14px] h-[14px]"
              checked={isConfirmed}
              onChange={() => setIsConfirmed(!isConfirmed)}
            />
            <label htmlFor={`confirm-button`} className="leading-4 text-[#212529] font-semibold font-lato pl-2 text-sm">
              I understand that deleting is permanent and can&apos;t be undone
            </label>
          </div>
          <div className="flex text-[13px] pt-4 pb-4 px-4 bg-white rounded-b-lg border-x">
            <button
              className="w-20 sm:w-40 py-3 px-5 h-8 sm:h-11 bg-green-600 text-white border border-[#e7eaec] ml-auto rounded-md"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className={` ${
                isConfirmed ? 'bg-red-main' : 'bg-buttonDisabledBg'
              } w-24 sm:w-40 h-8 sm:h-11 py-3 px-5 text-white ml-3 rounded-md`}
              onClick={DeleteVideo}
            >
              Delete Forever
            </button>
          </div>
        </>
      ) : (
        <div
          className={`flex text-base pb-10 px-4 ${
            permissionError ? 'bg-[#ffe3e9]' : 'bg-white'
          } rounded-b-lg border-x mx-auto justify-center`}
        >
          <WarningIcon className="w-5 h-5 fill-red-main" />
          <p className="text-center ml-4">You don’t have permissions to delete this video</p>
        </div>
      )}
    </div>
  );
}
